import { useEffect, useRef } from 'react';

/**
 * POS-I266: remember the element that had focus when a dialog was opened and give focus back to it
 * when the dialog closes (Esc, Cancel, X, or the caller unmounting it). Works for dialogs that are
 * mounted only while open and for dialogs that stay mounted with an `open` flag.
 */
export function useRestoreFocus(open: boolean): void {
  const opener = useRef<HTMLElement | null>(null);
  // Captured during render of the opening pass, before the dialog moves focus into itself.
  if (open && opener.current === null && typeof document !== 'undefined') {
    const el = document.activeElement;
    if (el instanceof HTMLElement && el !== document.body) opener.current = el;
  }
  useEffect(() => {
    if (open) return undefined;
    restoreFocus(opener);
    return undefined;
  }, [open]);
  useEffect(() => () => restoreFocus(opener), []);
}

/** Openers queued in the same tick (a dialog and the confirm dialog stacked on it), outermost first. */
let queued: HTMLElement[] = [];
/** Exit animation of the PrimeReact dialog; the late check runs after it has finished. */
export const RESTORE_SETTLE_MS = 350;

/**
 * POS-I278: once the closing dialogs are gone, focus the first queued opener that is still on the page, but only
 * when focus has fallen to the body, so it can never steal focus from something the user or the app focused.
 */
export function settleFocus(candidates: HTMLElement[]): void {
  if (typeof document === 'undefined') return;
  const active = document.activeElement;
  if (active && active !== document.body) return;
  const target = candidates.find((c) => c.isConnected);
  target?.focus({ preventScroll: true });
}

export function restoreFocus(ref: { current: HTMLElement | null }): void {
  const el = ref.current;
  ref.current = null;
  if (!el) return;
  const first = queued.length === 0;
  queued.push(el);
  // After the dialog's own hide handling has finished, so it cannot steal focus back.
  window.setTimeout(() => {
    if (el.isConnected) el.focus({ preventScroll: true });
  }, 0);
  if (first) {
    window.setTimeout(() => {
      const candidates = queued;
      queued = [];
      settleFocus(candidates);
    }, RESTORE_SETTLE_MS);
  }
}
